import type { Bundle, ProductQuota } from './types'
import { parseIsoDate } from './months'

/* The balance projections behind the Subscription and Additional Volume charts
   (MS-820), ported from the prototype's live forecast functions
   (subscription-portal.html: `mcpBalanceForecast`, `trialForecast`,
   `agtBalanceForecast`, `simForecastMcp`, `simForecastAgents`). Pure — every
   input is passed in, `today` included — so each is unit-testable without a
   clock, and the charts only draw.

   One deliberate improvement over the prototype's MCP math, kept from ADR §10:
   the prototype treats On Demand as one pot lapsing at a hardcoded month
   (`ON_DEMAND_EXPIRES_AFTER`); here each purchase is a real row with its own
   renewal window, so each month sums the purchases actually arriving then.

   The first projected month of every Agents chart is prorated by the days
   left in the current month (Q20 — the prototype's `monthRemainingFraction`):
   today's balance is today's, and deducting a whole month on the 1st of next
   month would charge for days already paid for. */

/** Current month plus the next twelve. */
export const SIM_MONTHS = 12

export interface ForecastMonth {
  date: Date
  /** Series values by key; absent means zero. */
  values: Record<string, number>
}

function monthStart(today: Date, offset: number): Date {
  return new Date(today.getFullYear(), today.getMonth() + offset, 1)
}

function monthEnd(start: Date): Date {
  return new Date(start.getFullYear(), start.getMonth() + 1, 0)
}

/** The share of the current month still ahead, today included. */
export function monthRemainingFraction(today: Date): number {
  const days = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  return (days - today.getDate() + 1) / days
}

/** The prototype's `burnStepTo`: the projected-consumption steppers walk a
 * clean 100,000 ladder from wherever the seeded average lands. */
export const BURN_STEP = 100_000
export function burnStepTo(current: number, direction: 1 | -1): number {
  return direction > 0
    ? (Math.floor(current / BURN_STEP) + 1) * BURN_STEP
    : Math.max(0, (Math.ceil(current / BURN_STEP) - 1) * BURN_STEP)
}

/** On Demand volume an MCP account receives in the month starting `start`:
 * every monthly-renewing purchase whose window overlaps it. A backfilled
 * legacy order (`renewsMonthly` false) is a one-off pot, not a monthly
 * arrival — its `remaining` is already in today's On Demand balance and is
 * not counted again in later months. */
export function onDemandForMonth(bundles: readonly Bundle[], start: Date): number {
  const end = monthEnd(start)
  return bundles
    .filter(
      (b) =>
        b.renewsMonthly &&
        parseIsoDate(b.startDate) <= end &&
        (b.endDate === null || parseIsoDate(b.endDate) >= start)
    )
    .reduce((sum, b) => sum + b.orderedQuantity, 0)
}

/** How many calendar months an MCP purchase made today delivers: the
 * purchase month in full, then monthly up to 12 in all, never past the
 * contract end — the server's `on_demand_validity` (Q2). An open-ended
 * contract gets all 12; never fewer than the purchase month itself. */
export function mcpDeliveredMonths(today: Date, contractEnd: string | null): number {
  if (contractEnd === null) return SIM_MONTHS
  const end = parseIsoDate(contractEnd)
  const months = (end.getFullYear() - today.getFullYear()) * 12 + (end.getMonth() - today.getMonth()) + 1
  return Math.min(SIM_MONTHS, Math.max(1, months))
}

/** Today's position of an MCP balance, split the way the chart stacks it:
 * consumed, then what is left of the contracted pot, then of On Demand.
 * Holds are charged to the contracted pot first — contracted volume is
 * consumed first, so the On Demand pot is what survives (prototype
 * `mcpVolumeSplit`). */
export function mcpPosition(quota: ProductQuota): { consumed: number; contractedLeft: number; onDemandLeft: number } {
  const split = quota.balanceByChannel
  if (!split) return { consumed: 0, contractedLeft: 0, onDemandLeft: 0 }
  const contractedLeft = Math.max(0, split.orderForm - split.held)
  const onDemandLeft = Math.max(0, split.onDemand - Math.max(0, split.held - split.orderForm))
  const consumed = Math.max(0, split.capacity - contractedLeft - onDemandLeft)
  return { consumed, contractedLeft, onDemandLeft }
}

/** Invocations Balance (Subscription, MCP Contracted): this month at its full
 * budget — consumed plus what is left — then each later month's allowance:
 * the included volume the award engine mints again each month, plus what is
 * left of the committed pot carried forward (it is minted once for the whole
 * contract, so `committedVolume` is never re-added), plus whatever On Demand
 * volume still arrives. */
export function mcpBalanceForecast(quota: ProductQuota, today: Date): ForecastMonth[] {
  const position = mcpPosition(quota)
  const contracted = quota.includedVolume + (quota.balanceByChannel?.orderFormCarriedOver ?? 0)
  return Array.from({ length: SIM_MONTHS + 1 }, (_, i) => {
    const date = monthStart(today, i)
    return {
      date,
      values:
        i === 0
          ? { consumed: position.consumed, contracted: position.contractedLeft, onDemand: position.onDemandLeft }
          : { consumed: 0, contracted, onDemand: onDemandForMonth(quota.bundles, date) },
    }
  })
}

/** A trial: one allowance that does not renew — this month, then nothing
 * (prototype `trialForecast`). */
export function trialBalanceForecast(quota: ProductQuota, today: Date): ForecastMonth[] {
  const left = Math.max(0, quota.trialAllowance - quota.trialConsumed)
  return Array.from({ length: SIM_MONTHS + 1 }, (_, i): ForecastMonth => {
    const values: Record<string, number> =
      i === 0 ? { consumed: Math.min(quota.trialConsumed, quota.trialAllowance), trial: left } : {}
    return { date: monthStart(today, i), values }
  })
}

/** MCP purchase simulation: the balance chart plus the cart, live for 12
 * calendar months from this one and never past the contract end (Q2) —
 * `contractEnd` null for an open-ended contract. On trial the base is the
 * trial chart (`simForecastMcp`'s trial branch). */
export function mcpPurchaseForecast(
  quota: ProductQuota,
  today: Date,
  cartVolume: number,
  contractEnd: string | null,
  trial: boolean
): ForecastMonth[] {
  const base = trial ? trialBalanceForecast(quota, today) : mcpBalanceForecast(quota, today)
  const end = contractEnd === null ? null : parseIsoDate(contractEnd)
  return base.map((month, i) => ({
    date: month.date,
    values: {
      ...month.values,
      sim: i < SIM_MONTHS && (trial || end === null || month.date <= end) ? cartVolume : 0,
    },
  }))
}

/** True in the months the annual Agents allowance is credited again: the
 * contract anniversary's month, in any year after the contract started. */
function isAnniversary(date: Date, contractStart: Date | null): boolean {
  return (
    contractStart !== null &&
    date.getMonth() === contractStart.getMonth() &&
    date.getFullYear() > contractStart.getFullYear()
  )
}

/** True for a month that starts after the contract end — no anniversary
 * credit arrives then, and On Demand credits (valid to the contract end, Q5)
 * are gone. An open-ended contract (`end` null) never ends. */
function afterContractEnd(date: Date, end: Date | null): boolean {
  return end !== null && date > end
}

/** Credits Balance (Subscription, Agents Contracted; prototype
 * `agtBalanceForecast`): the balance at the START of each month — today's
 * real balance, then a projection running down at `burn` a month and
 * stepping back up by the annual allowance (`included_volume`, the figure the
 * award engine credits; the committed pot is minted once and is already in
 * today's balance) on each contract anniversary up to the contract end. */
export function agentsBalanceForecast(
  balanceToday: number,
  annualAllowance: number,
  contractStart: string | null,
  contractEnd: string | null,
  burn: number,
  today: Date
): ForecastMonth[] {
  const anniversary = contractStart === null ? null : parseIsoDate(contractStart)
  const end = contractEnd === null ? null : parseIsoDate(contractEnd)
  const firstStep = burn * monthRemainingFraction(today)
  let balance = balanceToday
  return Array.from({ length: SIM_MONTHS + 1 }, (_, i): ForecastMonth => {
    const date = monthStart(today, i)
    if (i === 0) return { date, values: { starting: balance } }
    balance = Math.max(0, balance - (i === 1 ? firstStep : burn))
    const credited = isAnniversary(date, anniversary) && !afterContractEnd(date, end) ? annualAllowance : 0
    balance += credited
    return { date, values: { base: balance - credited, credited } }
  })
}

/** Agents purchase simulation (prototype `simForecastAgents`): the same
 * runway, with the cart's credits as the extra balance they buy — valid
 * until the contract end (Q5), so from the first month after it the
 * purchase adds nothing and no anniversary credit arrives (ADR §43). */
export function agentsPurchaseForecast(
  balanceToday: number,
  annualAllowance: number,
  contractStart: string | null,
  contractEnd: string | null,
  burn: number,
  cartVolume: number,
  today: Date
): ForecastMonth[] {
  const anniversary = contractStart === null ? null : parseIsoDate(contractStart)
  const end = contractEnd === null ? null : parseIsoDate(contractEnd)
  const firstStep = burn * monthRemainingFraction(today)
  let base = balanceToday
  let withBuy = balanceToday + cartVolume
  return Array.from({ length: SIM_MONTHS + 1 }, (_, i) => {
    const date = monthStart(today, i)
    if (i > 0) {
      const ended = afterContractEnd(date, end)
      const credited = isAnniversary(date, anniversary) && !ended ? annualAllowance : 0
      const step = i === 1 ? firstStep : burn
      base = Math.max(0, base - step) + credited
      // The bought credits lapse at the contract end: what is left of them
      // is lost, so the runway with the purchase falls back to the base.
      withBuy = ended ? base : Math.max(0, withBuy - step) + credited
    }
    return {
      date,
      values: { starting: i === 0 ? base : 0, base: i === 0 ? 0 : base, sim: Math.max(0, withBuy - base) },
    }
  })
}

/** Agents trial simulation (MS-820 Q21 — no prototype reference, an ADR
 * deviation): §24f-trial-i's "Trial volume" applied to the depleting
 * balance. The trial month draws what is left of the allowance down by the
 * projected consumption; once the trial has ended the trial balance is zero,
 * with no anniversary credit, and later months hold only the cart — which
 * absorbs whatever consumption the trial allowance could not. */
export function agentsTrialPurchaseForecast(
  quota: ProductQuota,
  burn: number,
  cartVolume: number,
  today: Date
): ForecastMonth[] {
  const trialEnd = quota.trialEndDate === null ? null : parseIsoDate(quota.trialEndDate)
  const firstStep = burn * monthRemainingFraction(today)
  let trial = Math.max(0, quota.trialAllowance - quota.trialConsumed)
  let cart = cartVolume
  return Array.from({ length: SIM_MONTHS + 1 }, (_, i) => {
    const date = monthStart(today, i)
    if (i > 0) {
      const step = i === 1 ? firstStep : burn
      const trialLive = trialEnd !== null && date <= trialEnd
      const fromTrial = trialLive ? Math.min(trial, step) : 0
      trial = trialLive ? trial - fromTrial : 0
      cart = Math.max(0, cart - (step - fromTrial))
    }
    return { date, values: { trial, sim: cart } }
  })
}
